import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Gamepad2, ChevronRight, CheckCircle2, XCircle, Mail, Facebook, MessageSquare, Copy, Check, Lightbulb, ZoomIn, ZoomOut, Move } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { API_URL } from '../config/api';

const BLUR_LEVELS = [20, 12, 7, 4, 2];
const SITE_URL = 'https://casitaplex.tv';

function buildShareText(result, game) {
  const date = game?.date || new Date().toISOString().split('T')[0];
  const grid = [0, 1, 2, 3, 4].map(i => {
    const a = result.attempts?.[i];
    if (!a) return '\u2b1b';
    return a.correct ? '\ud83d\udfe9' : '\ud83d\udfe5';
  }).join('');

  const lines = [
    `Film in Five \u2013 ${date}`,
    grid,
    result.won
      ? `Score: ${result.score}/5 \u2013 Guessed in ${result.attempts?.length || 0}!`
      : `Participated! +${result.score || 0} pts`,
    '',
    `Can you beat me? Play at ${SITE_URL}/play`,
  ];
  return lines.join('\n');
}

const SharePanel = ({ result, game }) => {
  const [copied, setCopied] = useState(false);
  const text = buildShareText(result, game);
  const gameDate = game?.date || new Date().toISOString().split('T')[0];
  // OG metadata URL for Facebook (absolute path needed for crawlers)
  const ogShareUrl = `${SITE_URL}/api/share/game/${gameDate}`;
  // Human-facing URL for Copy/WhatsApp (already in share text as login link)
  const copyText = text;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = copyText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(copyText)}`, '_blank');
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(`Film in Five \u2013 ${game?.date || 'Daily Game'}`);
    const body = encodeURIComponent(copyText);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const handleFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ogShareUrl)}&quote=${encodeURIComponent(text)}`,
      '_blank',
      'width=600,height=400'
    );
  };

  return (
    <div className="space-y-3" data-testid="share-panel">
      {/* Share text preview */}
      <div className="bg-zinc-800 rounded-lg p-3 text-sm text-gray-300 whitespace-pre-line leading-relaxed border border-zinc-700" style={{ fontFamily: 'inherit' }}>
        {text}
      </div>

      {/* Share buttons */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={handleCopy}
          className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-medium transition-colors border border-zinc-700"
          data-testid="share-copy-link"
        >
          {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
          {copied ? 'Copied!' : 'Copy Link'}
        </button>
        <button
          onClick={handleWhatsApp}
          className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg bg-green-700/80 hover:bg-green-700 text-white text-sm font-medium transition-colors"
          data-testid="share-whatsapp"
        >
          <MessageSquare className="h-4 w-4" />
          WhatsApp
        </button>
        <button
          onClick={handleEmail}
          className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg bg-blue-700/80 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
          data-testid="share-email"
        >
          <Mail className="h-4 w-4" />
          Email
        </button>
        <button
          onClick={handleFacebook}
          className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg bg-[#1877F2]/80 hover:bg-[#1877F2] text-white text-sm font-medium transition-colors"
          data-testid="share-facebook"
        >
          <Facebook className="h-4 w-4" />
          Facebook
        </button>
      </div>
    </div>
  );
};

const DailyGames = ({ user, isOpen, onClose, onGameComplete }) => {
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [guess, setGuess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(null);
  const [revealedHints, setRevealedHints] = useState([]);
  const [newHintIndex, setNewHintIndex] = useState(-1);
  const [nearMissMsg, setNearMissMsg] = useState('');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0 });
  const panOffsetStart = useRef({ x: 0, y: 0 });
  const posterContainerRef = useRef(null);

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 3));
  };
  const handleZoomOut = () => {
    setZoomLevel(prev => {
      const next = Math.max(prev - 0.5, 1);
      if (next === 1) setPanOffset({ x: 0, y: 0 });
      return next;
    });
  };
  const handlePanStart = (e) => {
    if (zoomLevel <= 1) return;
    e.preventDefault();
    setIsPanning(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    panStart.current = { x: clientX, y: clientY };
    panOffsetStart.current = { ...panOffset };
  };
  const handlePanMove = useCallback((e) => {
    if (!isPanning) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const dx = clientX - panStart.current.x;
    const dy = clientY - panStart.current.y;
    const maxPan = (zoomLevel - 1) * 80;
    setPanOffset({
      x: Math.max(-maxPan, Math.min(maxPan, panOffsetStart.current.x + dx)),
      y: Math.max(-maxPan, Math.min(maxPan, panOffsetStart.current.y + dy)),
    });
  }, [isPanning, zoomLevel]);
  const handlePanEnd = useCallback(() => { setIsPanning(false); }, []);

  useEffect(() => {
    if (isPanning) {
      window.addEventListener('mousemove', handlePanMove);
      window.addEventListener('mouseup', handlePanEnd);
      window.addEventListener('touchmove', handlePanMove, { passive: false });
      window.addEventListener('touchend', handlePanEnd);
      return () => {
        window.removeEventListener('mousemove', handlePanMove);
        window.removeEventListener('mouseup', handlePanEnd);
        window.removeEventListener('touchmove', handlePanMove);
        window.removeEventListener('touchend', handlePanEnd);
      };
    }
  }, [isPanning, handlePanMove, handlePanEnd]);

  const fetchGame = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
    try {
      const res = await axios.get(`${API_URL}/api/games/daily?user_id=${user.id}`);
      setGame(res.data);
      // Always show first hint as a starting clue, then one more per wrong guess
      const attemptLen = res.data.session?.attempts?.length || 0;
      if (res.data.hints && res.data.hints.length > 0) {
        setRevealedHints(res.data.hints.slice(0, attemptLen + 1));
      } else {
        setRevealedHints([]);
      }
      setNewHintIndex(-1);
      if (res.data.session?.completed) {
        setResult({
          completed: true,
          won: res.data.session.won,
          score: res.data.session.score,
          answer: res.data.answer,
          attempts: res.data.session.attempts,
        });
      }
    } catch (err) {
      console.error('Failed to fetch game:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/games/history?user_id=${user.id}&limit=10`);
      setHistory(res.data);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  }, [user.id]);

  useEffect(() => {
    if (isOpen) {
      fetchGame();
      fetchHistory();
    }
  }, [isOpen, fetchGame, fetchHistory]);

  const submitGuess = async (guessText) => {
    if (submitting) return;
    const trimmed = (guessText || '').trim();
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/games/daily/guess?user_id=${user.id}&guess=${encodeURIComponent(trimmed || '__skip__')}`
      );
      const data = res.data;
      if (data.completed) {
        const completionData = {
          completed: true,
          won: data.correct || data.status === 'correct',
          score: data.score,
          answer: data.answer,
          attempts: data.attempts || (game?.session?.attempts || []).concat([{ guess: guessText, correct: data.correct, attempt_num: data.attempt_num }]),
        };
        setResult(completionData);
        // Cache completion in localStorage for bulletproof widget updates
        try {
          localStorage.setItem('casitaplex-game-today', JSON.stringify({
            date: game?.date,
            ...completionData,
          }));
        } catch (_) { /* localStorage unavailable */ }
        fetchHistory();
        // Notify parent immediately + delayed retries for mobile
        if (onGameComplete) {
          onGameComplete();
          setTimeout(() => onGameComplete(), 600);
          setTimeout(() => onGameComplete(), 2000);
        }
      } else {
        // Show near-miss feedback
        if (data.near_miss) {
          setNearMissMsg("Almost! You're very close — try again!");
          setTimeout(() => setNearMissMsg(''), 3000);
        } else {
          setNearMissMsg('');
        }
        // Update revealed hints — offset by +1 since first hint is pre-shown
        if (game?.hints) {
          const newCount = (data.attempt_num || attemptCount + 1) + 1;
          setRevealedHints(game.hints.slice(0, newCount));
          setNewHintIndex(newCount - 1 < game.hints.length ? newCount - 1 : -1);
        } else if (data.revealed_hints && data.revealed_hints.length > 0) {
          setRevealedHints(data.revealed_hints);
          setNewHintIndex(data.revealed_hints.length - 1);
        } else if (data.hint) {
          setRevealedHints(prev => {
            if (!prev.includes(data.hint)) {
              setNewHintIndex(prev.length);
              return [...prev, data.hint];
            }
            return prev;
          });
        }

        setGame(prev => ({
          ...prev,
          session: {
            ...prev.session,
            attempts: [...(prev.session?.attempts || []), { guess: guessText, correct: false, near_miss: data.near_miss || false, attempt_num: data.attempt_num }],
            attempt_count: data.attempt_num,
          },
        }));
      }
      setGuess('');
    } catch (err) {
      console.error('Failed to submit guess:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Clear new hint animation after delay
  useEffect(() => {
    if (newHintIndex >= 0) {
      const timer = setTimeout(() => setNewHintIndex(-1), 2000);
      return () => clearTimeout(timer);
    }
  }, [newHintIndex]);

  const attemptCount = game?.session?.attempts?.length || 0;
  const currentBlur = attemptCount < 5 ? BLUR_LEVELS[attemptCount] : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-lg bg-zinc-900 border-zinc-700 text-white p-0 rounded-xl max-h-[90dvh] overflow-y-auto" style={{ gap: 0, WebkitOverflowScrolling: 'touch' }}>
        <DialogTitle className="sr-only">Film in Five - Daily Movie Game</DialogTitle>
        <DialogDescription className="sr-only">Guess the movie from its blurred poster in five attempts</DialogDescription>
        {/* Header */}
        <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b border-zinc-700 sticky top-0 bg-zinc-900 z-10">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <Gamepad2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm sm:text-base font-bold leading-tight" data-testid="daily-games-title">Film in Five</h2>
              <p className="text-[10px] sm:text-[11px] text-gray-400 leading-tight">Guess the movie from its blurred poster</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 sm:p-1.5 rounded-lg hover:bg-zinc-700 text-gray-400" data-testid="daily-games-close">
            <X className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin h-8 w-8 border-2 border-purple-500 border-t-transparent rounded-full" />
          </div>
        ) : !game ? (
          <div className="p-6 text-center text-gray-400 space-y-3">
            <p>{loadError ? 'Failed to load today\'s game.' : 'No game available today. Check back later!'}</p>
            <Button
              onClick={fetchGame}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6"
              data-testid="game-retry-btn"
            >
              Try Again
            </Button>
          </div>
        ) : (
          <div className="p-3 sm:p-4 space-y-2 sm:space-y-3">
            {/* ====== ACTIVE GAME ====== */}
            {!result ? (
              <>
                {/* Blurred Poster — Zoomable: pinch/buttons to zoom, drag to pan, blur always active */}
                <div
                  ref={posterContainerRef}
                  className="relative rounded-xl overflow-hidden bg-zinc-800 aspect-[2/3] max-h-[28vh] sm:max-h-[50vh] mx-auto w-full max-w-[160px] sm:max-w-[260px]"
                  data-testid="game-poster-container"
                  onContextMenu={(e) => e.preventDefault()}
                  style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none', cursor: zoomLevel > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default' }}
                >
                  <img
                    src={game.poster_url}
                    alt=""
                    className="w-full h-full object-cover transition-[filter] duration-700 pointer-events-none"
                    style={{
                      filter: `blur(${currentBlur}px)`,
                      transform: `scale(${1.15 * zoomLevel}) translate(${panOffset.x / zoomLevel}px, ${panOffset.y / zoomLevel}px)`,
                      userSelect: 'none',
                      WebkitUserDrag: 'none',
                      transition: isPanning ? 'filter 0.7s' : 'filter 0.7s, transform 0.2s ease-out',
                    }}
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                    onContextMenu={(e) => e.preventDefault()}
                    data-testid="game-poster-image"
                  />
                  {/* Anti-cheat overlay — also handles pan gestures */}
                  <div
                    className="absolute inset-0 z-10"
                    style={{ background: 'transparent', pointerEvents: 'all', touchAction: 'none' }}
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                    onMouseDown={handlePanStart}
                    onTouchStart={handlePanStart}
                    draggable={false}
                    data-testid="poster-protection-overlay"
                  />
                  {/* Zoom controls */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5" data-testid="poster-zoom-controls">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleZoomOut(); }}
                      disabled={zoomLevel <= 1}
                      className="w-7 h-7 rounded-full bg-black/70 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/90 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
                      data-testid="poster-zoom-out"
                    >
                      <ZoomOut className="h-3.5 w-3.5" />
                    </button>
                    {zoomLevel > 1 && (
                      <span className="text-[10px] font-bold text-white bg-black/70 backdrop-blur-sm rounded-full px-2 py-0.5" data-testid="poster-zoom-level">
                        {zoomLevel.toFixed(1)}x
                      </span>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleZoomIn(); }}
                      disabled={zoomLevel >= 3}
                      className="w-7 h-7 rounded-full bg-black/70 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/90 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
                      data-testid="poster-zoom-in"
                    >
                      <ZoomIn className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="absolute top-2 right-2 z-20 bg-black/70 backdrop-blur-sm rounded-full px-2.5 py-0.5 text-[11px] font-bold text-purple-400">
                    {5 - attemptCount} left
                  </div>
                  {zoomLevel > 1 && (
                    <div className="absolute top-2 left-2 z-20 bg-black/70 backdrop-blur-sm rounded-full px-2 py-0.5 flex items-center gap-1">
                      <Move className="h-3 w-3 text-gray-300" />
                      <span className="text-[10px] text-gray-300">Drag to pan</span>
                    </div>
                  )}
                </div>

                {/* Attempts indicator */}
                <div className="flex justify-center gap-1.5" data-testid="attempts-indicator">
                  {[0, 1, 2, 3, 4].map(i => (
                    <div
                      key={i}
                      className={`w-7 sm:w-8 h-1.5 rounded-full transition-colors ${
                        i < attemptCount
                          ? (game.session?.attempts?.[i]?.correct ? 'bg-green-500' : 'bg-red-500')
                          : i === attemptCount ? 'bg-purple-500' : 'bg-zinc-700'
                      }`}
                    />
                  ))}
                </div>

                {/* Hints — revealed after each wrong guess */}
                {revealedHints.length > 0 && (
                  <div className="space-y-1" data-testid="hints-container">
                    {revealedHints.map((hint, i) => (
                      <div
                        key={i}
                        className={`flex items-start gap-2 text-xs sm:text-sm bg-zinc-800 rounded-lg px-3 py-2 border transition-all duration-500 ${
                          i === newHintIndex
                            ? 'border-purple-500/60 bg-purple-900/20 text-purple-300 animate-pulse'
                            : 'border-transparent text-gray-400'
                        }`}
                        data-testid={`hint-${i}`}
                      >
                        <Lightbulb className={`h-3.5 w-3.5 flex-shrink-0 mt-0.5 ${i === newHintIndex ? 'text-purple-400' : 'text-purple-500/60'}`} />
                        <span className="leading-snug">{hint}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Previous wrong guesses */}
                {game.session?.attempts?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="wrong-guesses">
                    {game.session.attempts.map((a, i) => (
                      <span key={i} className={`px-2.5 py-1 rounded-full text-[11px] flex items-center gap-1 ${
                        a.near_miss
                          ? 'bg-amber-900/30 border border-amber-700 text-amber-400'
                          : 'bg-red-900/30 border border-red-800 text-red-400'
                      }`}>
                        <XCircle className="h-3 w-3" />
                        <span className="truncate max-w-[100px]">{a.guess}</span>
                      </span>
                    ))}
                  </div>
                )}

                {/* Near-miss feedback */}
                {nearMissMsg && (
                  <div className="text-center py-1.5 px-3 bg-amber-900/20 border border-amber-800/50 rounded-lg animate-pulse" data-testid="near-miss-msg">
                    <p className="text-amber-400 text-xs font-medium">{nearMissMsg}</p>
                  </div>
                )}

                {/* Guess Input */}
                <div data-testid="guess-input-container">
                  <div className="flex gap-2">
                    <Input
                      value={guess}
                      onChange={(e) => setGuess(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && submitGuess(guess)}
                      placeholder="Movie title..."
                      className="flex-1 min-w-0 bg-zinc-800 border-zinc-600 text-white placeholder-gray-500 h-10 text-sm"
                      data-testid="guess-input"
                    />
                    <Button
                      onClick={() => submitGuess(guess || '')}
                      disabled={submitting}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-5 h-10 text-sm font-semibold flex-shrink-0"
                      data-testid="guess-submit-btn"
                    >
                      {submitting ? '...' : 'Guess'}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              /* ====== RESULT SCREEN ====== */
              <div className="text-center space-y-2 sm:space-y-3 py-1 sm:py-2" data-testid="game-result">
                {/* Revealed Poster */}
                <div className="relative rounded-xl overflow-hidden mx-auto w-28 sm:w-44 aspect-[2/3]">
                  <img src={game.poster_url} alt={result.answer} className="w-full h-full object-cover" />
                </div>

                <h3 className="text-lg sm:text-xl font-bold text-white">{result.answer}</h3>

                {result.won ? (
                  <div className="space-y-1.5">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-900/30 border border-green-700 rounded-full text-sm">
                      <CheckCircle2 className="h-4 w-4 text-green-400" />
                      <span className="text-green-400 font-semibold">Correct!</span>
                    </div>
                    <p className="text-2xl sm:text-3xl font-black text-purple-400" data-testid="game-score">+{result.score} pts</p>
                    <p className="text-gray-400 text-xs sm:text-sm">
                      Guessed in {result.attempts?.length || 0} attempt{(result.attempts?.length || 0) !== 1 ? 's' : ''}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-900/30 border border-red-700 rounded-full text-sm">
                      <XCircle className="h-4 w-4 text-red-400" />
                      <span className="text-red-400 font-semibold">Better luck tomorrow!</span>
                    </div>
                    <p className="text-lg sm:text-xl font-black text-purple-400" data-testid="game-participation-score">+{result.score || 0} pts</p>
                    <p className="text-gray-400 text-xs sm:text-sm">
                      Participation points earned
                    </p>
                  </div>
                )}

                {/* Attempt Summary */}
                {result.attempts && result.attempts.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-1.5 pt-1">
                    {result.attempts.map((a, i) => (
                      <div key={i} className={`px-2.5 py-1 rounded-full text-[11px] font-medium truncate max-w-[120px] ${
                        a.correct ? 'bg-green-900/40 text-green-400 border border-green-700' : 'bg-zinc-800 text-gray-400 border border-zinc-700'
                      }`}>
                        {a.guess}
                      </div>
                    ))}
                  </div>
                )}

                {/* ====== SHARE ====== */}
                <div className="pt-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">Share Your Result</p>
                  <SharePanel result={result} game={game} />
                </div>

                <Button onClick={onClose} className="mt-2 bg-zinc-700 hover:bg-zinc-600 text-white w-full sm:w-auto" data-testid="game-done-btn">
                  Done
                </Button>
              </div>
            )}

            {/* Stats & History */}
            {history && history.stats && (
              <div className="border-t border-zinc-700 pt-2 sm:pt-3 mt-2 sm:mt-3">
                <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 sm:mb-2">Your Stats</h3>
                <div className="grid grid-cols-4 gap-1 sm:gap-2" data-testid="game-stats">
                  {[
                    { val: history.stats.total_played, label: 'Played', color: 'text-white' },
                    { val: `${history.stats.win_rate}%`, label: 'Win Rate', color: 'text-green-400' },
                    { val: history.stats.current_streak, label: 'Streak', color: 'text-purple-400' },
                    { val: history.stats.total_score, label: 'Total Pts', color: 'text-white' },
                  ].map((s, i) => (
                    <div key={i} className="bg-zinc-800 rounded-lg p-1.5 sm:p-3 text-center">
                      <p className={`text-sm sm:text-xl font-bold ${s.color}`}>{s.val}</p>
                      <p className="text-[9px] sm:text-xs text-gray-500">{s.label}</p>
                    </div>
                  ))}
                </div>

                {history.sessions && history.sessions.length > 0 && (
                  <div className="mt-2 sm:mt-2.5 space-y-1 sm:space-y-1.5">
                    <h4 className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide">Recent Games</h4>
                    {history.sessions.slice(0, 3).map((session, i) => (
                      <div key={i} className="flex items-center gap-2 sm:gap-2.5 bg-zinc-800 rounded-lg p-1.5 sm:p-2">
                        {session.poster_url && (
                          <img src={session.poster_url} alt="" className="w-6 h-9 sm:w-7 sm:h-10 rounded object-cover flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] sm:text-sm text-white truncate">{session.game_title || 'Unknown'}</p>
                          <p className="text-[9px] sm:text-xs text-gray-500">{session.date}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          {session.won ? (
                            <span className="text-green-400 text-[11px] sm:text-sm font-semibold">+{session.score}</span>
                          ) : (
                            <span className="text-red-400 text-[10px] sm:text-[11px]">Miss</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DailyGames;
